import { PageContainer, Text, VStack } from '@/components/Atoms'
import { AgreementId } from '@/components/DataTable/CellContent/AgreementId'
import { MasterAgreement } from '@/components/DataTable/CellContent/MasterAgreement'
import NoDataAvailable from '@/components/DataTable/CellContent/NoDataAvailable'
import { TableHeader } from '@/components/DataTable/CellContent/TableHeader'
import { useCrtCurrencySync } from '@/hooks/crt/useCrtCurrencySync'
import {
  resolveComparison,
  useAgreementVariance,
  type AgreementVariance,
} from '@/hooks/queries/crt/useVarianceAnalysis'
import { VARIANCE_GROUPING_DIMENSION_TYPES } from '@repo/constants'
import { createFileRoute } from '@tanstack/react-router'
import { type ColumnDef } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { CRT_ROUTES, toStaticData } from '../crt-route-config'
import { DrillDataTable } from './-components/DrillDataTable'
import { DrillPageHeader } from './-components/DrillPageHeader'
import { DrillRowActions, type DrillTarget } from './-components/DrillRowActions'
import { drillRoutesFor, TARGET_LABEL_KEYS, type DrillRow } from './-data/drill-dimensions'
import {
  buildRowDrillSearch,
  decodeDrillValue,
  drillSearchSchema,
  requireComparisonSides,
  toCostTypeCellScope,
  toDrillFilters,
} from './-data/drill-search'

export const Route = createFileRoute('/_auth/_modules/crt/variance-analysis/by-agreement')({
  staticData: toStaticData(CRT_ROUTES['/crt/variance-analysis/by-agreement']),
  component: RouteComponent,
  validateSearch: (search) => drillSearchSchema.parse(search),
  beforeLoad: ({ search }) => requireComparisonSides(search),
})

/**
 * The endpoint's rows as the drill table reads them: a parent per master agreement, each holding the
 * agreements underneath it.
 *
 * Two translations, and only two. `NONE_VALUE` decodes back to `null` on `masterAgreement`, which is
 * what the cell renders its "unattributed" reading from — the endpoint carries the sentinel because a
 * URL cannot carry a `null`, and the table has no such constraint. And the endpoint's
 * `agreementDescription` becomes `labels.agreementNumber`: the drill vocabulary keeps a dimension's
 * code in `grouping` and its human-readable name in `labels`, so a description is the *label of the
 * agreement number* rather than a dimension of its own.
 *
 * `agreementNumber` is **not** decoded: it is already a real `string | null`, null only on a parent row,
 * and running it through the sentinel decode would turn a genuine agreement literally named `NONE` into
 * a blank cell.
 */
function toAgreementDrillRows(data: AgreementVariance | undefined): DrillRow[] {
  if (!data) return []

  return data.results.map((row) => ({
    grouping: { masterAgreement: decodeDrillValue(row.grouping.masterAgreement) ?? null },
    variance: row.variance,
    subRows: row.breakdown.map((entry) => ({
      grouping: {
        masterAgreement: decodeDrillValue(entry.grouping.masterAgreement) ?? null,
        agreementNumber: entry.grouping.agreementNumber,
      },
      labels: { agreementNumber: entry.grouping.agreementDescription },
      variance: entry.variance,
    })),
  }))
}

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  // `state: true` keeps the drill history (`drillFrom`) on the entry, as the header's own navigations do.

  const { conversion } = useCrtCurrencySync({
    search,
    navigate: (update) => navigate({ search: update, replace: true, state: true }),
  })

  const comparison = resolveComparison(search)
  const scope = toCostTypeCellScope(search)

  const { data, isPending, isPlaceholderData, error } = useAgreementVariance(comparison, scope)
  const rows = toAgreementDrillRows(data)

  /** Why the table has nothing to show, when it has nothing to show. */
  const unavailableError = !comparison
    ? new Error(t('varianceAnalysis.emptyState'))
    : !scope
      ? new Error(t('varianceAnalysis.drill.byAgreement.unavailableScope'))
      : null

  /**
   * Where a row leads, asked of the same rule the endpoints refuse requests by.
   *
   * The filters are read off the **target** search rather than this page's — it is this page's plus the
   * row's own agreement, so the rule sees both what the reader already narrowed to and what following
   * the link would add. A reader who arrived from `/by-banner` carries a `hierarchyErp`, which leaves the
   * customer reading with nothing left to divide, and the row then offers nothing rather than a link that
   * comes back 422.
   */
  const targetsForRow = (row: DrillRow): DrillTarget[] => {
    const targetSearch = buildRowDrillSearch(search, row, ['masterAgreement', 'agreementNumber'])
    if (!targetSearch) return []

    return drillRoutesFor(toDrillFilters(targetSearch)).map((to) => ({
      to,
      search: targetSearch,
      label: t(TARGET_LABEL_KEYS[to]),
    }))
  }

  // A master agreement has no name anywhere in the schema — it is a bare code shared by many
  // agreements — so a parent row describes itself by what it is rather than by a description.
  const describeAgreement = (row: DrillRow) => {
    if (row.grouping.agreementNumber) return row.labels?.agreementNumber ?? undefined
    return row.grouping.masterAgreement
      ? t('varianceAnalysis.drill.table.masterAgreement')
      : t('varianceAnalysis.drill.table.masterAgreement_zero')
  }

  const leadingColumns: ColumnDef<DrillRow, unknown>[] = [
    {
      id: 'agreementNumber',
      // A parent is keyed on its master agreement and a child on its agreement, so one column carries
      // both; sorting runs within each level, so the two codes are never compared with each other.
      accessorFn: (row) =>
        row.grouping.agreementNumber ?? row.grouping.masterAgreement ?? undefined,
      sortUndefined: 'last',
      header: ({ column }) => <TableHeader column={column} sortable />,
      meta: { displayTitle: t('varianceAnalysis.drill.table.agreementNumber') },
      cell: ({ row }) => {
        const { agreementNumber, masterAgreement } = row.original.grouping
        if (agreementNumber) return <AgreementId id={agreementNumber} />
        // Null exactly when the agreement maps to no master agreement, which the endpoint reports
        // rather than drops: those dollars are real, they simply belong to nobody's contract.
        if (!masterAgreement) return <NoDataAvailable />

        return <MasterAgreement id={masterAgreement} />
      },
    },
    {
      id: 'agreementDescription',
      accessorFn: describeAgreement,
      sortUndefined: 'last',
      header: ({ column }) => <TableHeader column={column} sortable />,
      meta: { displayTitle: t('varianceAnalysis.drill.table.agreementDescription') },
      cell: ({ row, getValue }) => {
        const description = getValue<string | undefined>()
        if (!description) return <NoDataAvailable />

        return (
          <Text tone={row.depth === 0 ? 'muted' : undefined} maxLines="one">
            {description}
          </Text>
        )
      },
    },
  ]

  return (
    <PageContainer maxWidth="wide">
      <VStack gap="lg" align="stretch">
        <DrillPageHeader
          title={t('varianceAnalysis.drill.byAgreement.pageHeader')}
          search={search}
          dimension={VARIANCE_GROUPING_DIMENSION_TYPES.byAgreement}
        />

        <DrillDataTable
          search={search}
          dimension={VARIANCE_GROUPING_DIMENSION_TYPES.byAgreement}
          rows={rows}
          leadingColumns={leadingColumns}
          getSubRows={(row) => row.subRows}
          canHideEmptyRows
          renderRowActions={(row) => (
            <DrillRowActions
              from={VARIANCE_GROUPING_DIMENSION_TYPES.byAgreement}
              targets={targetsForRow(row.original)}
            />
          )}
          currency={conversion}
          isPending={isPending}
          isRefreshing={isPlaceholderData}
          error={error ?? unavailableError}
          // Investment spend, so more of it is money going out: the increase reads as
          // unfavourable here, the same way it does on the cost-type table above.
          isIncreaseFavourable={false}
        />
      </VStack>
    </PageContainer>
  )
}
